import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { autoUpdate, flip, offset, shift, useFloating, useInteractions, useRole, } from '@floating-ui/react';
import { Combobox, Portal, Transition } from '@headlessui/react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import { Loading } from '../Loading/Loading';
/**
 * Autocomplete component that uses Headless UI's Combobox component. Allows for querying and selecting from a list of options.
 * The properties for configuring this component can be found at {@link AutoCompleteProps}
 */
export const AutoComplete = ({ minWidth, disabled, label, required, onSelect, hideClearButton, error, helperText, defaultValue, value, children, dense, renderLabel, listBoxOptionsClassName, handleNoResults, alwaysShowAddNew, floatProps, extraMiddleware, onQueryChange, externalFilter, loading, allowChangeWhenLoading, queryValue, allowCustomValues, inputProps, variant = 'standard', focusFirstValueOnOpen, disablePortal = false, }) => {
    const [query, setQuery] = useState(queryValue || '');
    const [selected, setSelected] = useState(defaultValue || '');
    const [hasValue, setHasValue] = useState(query.length > 0 ? true : Array.isArray(selected) ? selected.length > 0 : selected !== '' && selected !== undefined);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const openRef = useRef(false);
    const portalRootZIndexRef = useRef(null);
    // Sync Combobox open state to dropdownOpen after commit (never set state during render).
    // No deps: run after every commit; setState bails out when value unchanged, so no infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync ref→state after each commit
    useEffect(() => {
        setDropdownOpen(openRef.current);
    });
    // When dropdown is open inside a Drawer (and using Portal), the default portal root can be behind the overlay.
    // Raise the portal root so the list receives pointer events. Skip when disablePortal (list renders inline).
    useEffect(() => {
        if (disablePortal || !dropdownOpen)
            return;
        const root = document.getElementById('headlessui-portal-root');
        if (!root)
            return;
        portalRootZIndexRef.current = root.style.zIndex || '';
        root.style.zIndex = '400';
        return () => {
            if (portalRootZIndexRef.current !== null) {
                root.style.zIndex = portalRootZIndexRef.current;
                portalRootZIndexRef.current = null;
            }
        };
    }, [disablePortal, dropdownOpen]);
    useEffect(() => {
        setHasValue(query.length > 0 ? true : Array.isArray(selected) ? selected.length > 0 : selected !== '' && selected !== undefined);
    }, [selected, query]);
    useEffect(() => {
        setSelected(value || '');
        if (!renderLabel) {
            if (!value) {
                setQuery('');
            }
            else {
                setQuery(renderValueLabel(children));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- children/renderValueLabel omitted intentionally; re-sync only when value changes
    }, [value]);
    useEffect(() => {
        if (queryValue === '') {
            setQuery('');
            return;
        }
        if (queryValue) {
            setQuery(queryValue);
            setHasValue(true);
        }
    }, [queryValue]);
    // When the parent provides renderLabel (e.g. selected address), sync it to query so the input
    // shows the selected display text. Headless UI Combobox binds the input value to query, so
    // without this the input stays on the typed filter text (e.g. "33") after selection.
    useEffect(() => {
        if (selected && renderLabel != null && String(renderLabel).trim() !== '') {
            setQuery(String(renderLabel));
        }
    }, [selected, renderLabel]);
    const handleSelect = (newValue, custom) => {
        if (newValue === 'hiddenFirstOption' || newValue === '' || newValue === undefined)
            return;
        if (custom) {
            setSelected('autocomplete-custom-value');
        }
        else {
            setSelected(newValue === null ? '' : newValue);
            if (newValue !== null) {
                const found = children.find((child) => child?.props?.value === newValue);
                const displayLabel = found?.props?.label
                    ? String(found.props.label)
                    : found?.props?.children
                        ? String(found.props.children)
                        : '';
                if (displayLabel)
                    setQuery(displayLabel);
            }
        }
        onSelect(newValue);
    };
    const renderValueLabel = (children) => {
        if (renderLabel)
            return String(renderLabel);
        if (value === undefined || value === '')
            return '';
        if (Array.isArray(value)) {
            let found = '';
            children.forEach((child) => {
                if (value.includes(child?.props?.value)) {
                    if (found.length > 0 && (child?.props?.label || child?.props?.children))
                        found += ', ';
                    found += `${child?.props?.label || child?.props?.children}`;
                }
            });
            return found;
        }
        const found = children.find((child) => child?.props?.value === value);
        if (found?.props?.label)
            return String(found.props.label);
        return found?.props?.children ? String(found.props.children) : '';
    };
    const handleChildFilter = () => {
        return children.filter((child) => (child.props.label && child.props.label.toLowerCase().includes(query.toLowerCase())) ||
            (child.props.children && child.props.children.toString().toLowerCase().includes(query.toLowerCase())));
    };
    const { refs, floatingStyles, context } = useFloating({
        transform: false, // needed for transitions to work properly
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(floatProps?.offset || 5),
            flip({ ...floatProps?.flip }),
            shift({ ...floatProps?.shift }),
            ...(extraMiddleware || []),
        ],
    });
    const role = useRole(context, { role: 'combobox' });
    const { getReferenceProps, getFloatingProps } = useInteractions([role]);
    const comboboxClassNames = () => {
        switch (variant) {
            case 'standard':
                return `relative w-full ${dense ? 'h-6' : `${helperText || Boolean(error) ? 'h-12' : 'h-10'}`}`;
            case 'filled':
                return `relative w-full`;
        }
    };
    const inputClassNames = (open) => {
        switch (variant) {
            case 'standard':
                return `border-blue-gray-200 text-blue-gray-700 disabled:bg-blue-gray-50 peer size-full rounded-[7px] border bg-white text-left font-normal outline outline-0 transition-all focus:border-2 focus:outline-0 disabled:border-0 ${dense ? 'px-1 py-1.5 text-xs' : 'px-3 py-2.5 text-base'}  
                        ${label ? 'focus:border-t-transparent' : ''} ${(hasValue || open) && label ? 'border border-t-transparent ' : ''} ${error
                    ? 'border-red-500 placeholder-shown:border-red-500 focus:border-red-500 '
                    : `border-blue-gray-200 focus:border-input-focus flex items-center `}`;
            case 'filled':
                return `text-blue-gray-700 border-blue-gray-200 focus:border-input-focus peer block w-full appearance-none rounded-t-lg border-0 border-b-2 bg-gray-50 px-2.5 pb-2.5 pt-5 text-base focus:outline-none focus:ring-0 ${error && 'border-red-500 focus:border-red-500'}`;
        }
    };
    const labelClassNames = (open) => {
        switch (variant) {
            case 'standard':
                return `${dense ? 'h-fit' : ''} before:content[' '] after:content[' '] text-blue-gray-400 peer-disabled:text-blue-gray-400 pointer-events-none absolute -top-1.5 left-0 flex size-full select-none overflow-clip font-normal transition-all before:pointer-events-none before:mr-1 before:mt-[6.5px] before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:transition-all after:pointer-events-none after:ml-1 after:mt-[6.5px] after:box-border after:block after:h-1.5 after:w-2.5 after:grow after:rounded-tr-md after:transition-all peer-focus:text-[11px] peer-focus:leading-tight peer-disabled:text-gray-500 peer-disabled:before:border-transparent peer-disabled:after:border-transparent ${label
                    ? 'peer-focus:before:border-l-2 peer-focus:before:border-t-2 peer-focus:after:border-r-2 peer-focus:after:border-t-2'
                    : ''} ${(hasValue || open) && label
                    ? 'before:border-blue-gray-200 after:border-blue-gray-200 text-[11px] leading-tight before:border-l before:border-t after:border-r after:border-t peer-disabled:text-gray-500 '
                    : `${dense ? 'text-xs leading-[2.9]' : 'text-base leading-[3.25]'} before:border-transparent after:border-transparent `} ${error
                    ? 'text-red-500 before:border-red-500 after:border-red-500 peer-placeholder-shown:text-red-500 peer-focus:text-red-500 peer-focus:before:border-red-500 peer-focus:after:border-red-500 '
                    : `text-blue-gray-400 before:border-blue-gray-200 after:border-blue-gray-200 peer-focus:text-input-focus peer-focus:before:border-input-focus peer-focus:after:border-input-focus `}`;
            case 'filled':
                return `text-blue-gray-500 peer-focus:text-input-focus absolute left-2.5 top-4 z-10 origin-[0] -translate-y-4 cursor-pointer text-base duration-300 ${error &&
                    'text-red-500 before:border-red-500 after:border-red-500 peer-placeholder-shown:text-red-500 peer-focus:text-red-500 peer-focus:before:border-red-500 peer-focus:after:border-red-500 '}`;
        }
    };
    return (_jsxs("div", { className: comboboxClassNames(), style: { minWidth: dense ? minWidth : minWidth || 200 }, children: [_jsx(Combobox, { value: selected, onChange: (value) => handleSelect(value), disabled: disabled || (loading && !allowChangeWhenLoading), children: ({ open }) => {
                    openRef.current = open;
                    return (_jsxs("div", { className: "relative", children: [_jsx("div", { ref: refs.setReference, ...getReferenceProps, className: variant === 'filled'
                                    ? `relative w-full`
                                    : `relative w-full ${dense ? 'h-6 rounded-[7px] bg-white' : `h-10`}`, style: { minWidth: dense ? minWidth : minWidth || 200 }, children: _jsxs(Combobox.Button, { className: 'w-full', children: [_jsx(Combobox.Input, { onBlur: (e) => {
                                                const next = e.relatedTarget;
                                                const focusMovedToFloating = next && typeof Node !== 'undefined' && refs.floating.current?.contains(next);
                                                const focusMovedToBody = next === document.body;
                                                // When dropdown is in a Portal (e.g. inside a Drawer), blur can move focus to
                                                // body before the option receives the click. Refocus the input so the list
                                                // stays open and the option click can fire.
                                                if (open &&
                                                    (focusMovedToBody || focusMovedToFloating) &&
                                                    refs.reference.current &&
                                                    'querySelector' in refs.reference.current) {
                                                    const input = refs.reference.current.querySelector('input');
                                                    if (input) {
                                                        setTimeout(() => input.focus(), 0);
                                                    }
                                                    return;
                                                }
                                                if (allowCustomValues && (selected === 'autocomplete-custom-value' || !selected)) {
                                                    handleSelect(query, true);
                                                }
                                            }, autoComplete: "off", className: inputClassNames(open), displayValue: () => renderValueLabel(children), onChange: (event) => {
                                                if (onQueryChange)
                                                    onQueryChange(event.currentTarget.value);
                                                if (allowCustomValues) {
                                                    setSelected('autocomplete-custom-value');
                                                }
                                                setQuery(event.currentTarget.value);
                                            }, ...inputProps, value: query }), loading ? (_jsx("div", { className: "absolute inset-y-0 right-0 flex items-center pr-2", children: _jsx(Loading, { size: "h-6 w-6", className: "text-sourgum-greyblue-900" }) })) : (!disabled &&
                                            !hideClearButton &&
                                            hasValue && (_jsx(XMarkIcon, { className: "absolute inset-y-2 right-2 size-6 cursor-pointer items-center text-gray-400", onClick: () => {
                                                handleSelect(null);
                                                if (onQueryChange)
                                                    onQueryChange('');
                                                setQuery('');
                                            } }))), _jsxs(Combobox.Label, { className: labelClassNames(open), children: [label, required && label && (_jsx("span", { className: `ml-0.5 inline-block ${disabled ? 'text-gray-400' : 'text-gray-500'}`, children: "*" }))] })] }) }), ((externalFilter ? children : handleChildFilter()).length > 0 || alwaysShowAddNew) &&
                                (() => {
                                    const optionsContent = (_jsx(Transition, { show: open, as: Fragment, leave: "transition ease-in duration-100", leaveFrom: "opacity-100", leaveTo: "opacity-0", children: _jsxs(Combobox.Options, { ref: refs.setFloating, style: {
                                                ...floatingStyles,
                                                minWidth: refs.reference.current?.getBoundingClientRect().width,
                                            }, ...getFloatingProps(), onMouseDown: (e) => {
                                                e.preventDefault(); // Keep input focus so list stays open until option click
                                            }, className: `z-[375] mt-1 max-h-56 overflow-auto rounded-md bg-white text-left shadow-lg ring-1 ring-black/5 focus:outline-none ${dense ? 'py-0 text-xs' : 'py-1 text-base'} ${listBoxOptionsClassName || ''}`, children: [!focusFirstValueOnOpen && (_jsx(Combobox.Option, { className: 'hidden', value: 'hiddenFirstOption', children: '' })), (externalFilter ? children : handleChildFilter()).map((child) => React.cloneElement(child, {
                                                    dense: dense,
                                                })), handleNoResults && ((query && handleChildFilter().length === 0) || alwaysShowAddNew) ? (_jsx(AutoCompleteOption, { value: query, onClick: () => {
                                                        handleNoResults.onClick(query);
                                                    }, icon: alwaysShowAddNew ? (_jsx(PlusCircleIcon, { className: `${dense ? 'size-4' : 'size-5'}` })) : undefined, forceIcon: alwaysShowAddNew, children: query && alwaysShowAddNew
                                                        ? `${handleNoResults.label} (${query})`
                                                        : `${handleNoResults.label}` })) : undefined] }) }));
                                    return disablePortal ? (optionsContent) : (_jsx(Portal, { as: 'div', autoFocus: false, children: optionsContent }));
                                })()] }));
                } }), error ? (_jsx("p", { className: "mx-3.5 mt-1 text-xs text-[#F44336]", children: error.message })) : (helperText && _jsx("p", { className: "mx-3.5 mt-1 text-xs text-black opacity-50", children: helperText }))] }));
};
export default AutoComplete;
export const AutoCompleteOption = ({ value, children, onClick, dense, disabled, icon, forceIcon, className, }) => {
    // sour-ui-select-option does nothing but allows it to be accessed via target
    return (_jsx(Combobox.Option, { className: ({ active }) => twMerge(`${disabled ? 'text-blue-gray-300' : active ? 'bg-primary-400 text-select-option-text-active' : 'text-blue-gray-900'} relative cursor-pointer select-none pr-9 ${dense ? 'py-1.5 pl-1' : 'py-2 pl-3'} sour-ui-select-option`, 'max-xs:max-w-xs', className), value: value, onMouseDown: (e) => {
            e.preventDefault(); // Keep input focus so list stays open until click selects
        }, onClick: (e) => {
            onClick?.(e);
        }, disabled: disabled, children: ({ selected, active }) => (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex items-center", children: _jsx("span", { className: `${selected && !disabled ? 'font-semibold' : 'font-normal'} block w-full truncate`, children: children }) }), ((selected && !disabled) || forceIcon) && (_jsx("span", { className: `${active ? 'text-select-option-text-active' : 'text-primary-400'} absolute inset-y-0 right-0 flex items-center pr-4`, children: icon ?? _jsx(CheckIcon, { className: `${dense ? 'size-4' : 'size-5'}`, "aria-hidden": "true" }) }))] })) }));
};
//# sourceMappingURL=AutoComplete.js.map